import { Box } from "@mui/material";
import { useState, useMemo } from "react";
import AddIcon from "@mui/icons-material/Add";
import { useSelector } from "react-redux";
import { useNavigate, useParams } from "react-router-dom";
import { Layout, Paper, Button } from "$components";
import { Switch } from "$components/Forms/Switch";
import {
  DataTableWithContext,
  EnumDataRowType,
  EnumFilterTypes,
} from "$components/DataTable";
import { usePopin } from "$components/Popin/usePopin";
import { useQueryParameters } from "$hooks/useQueryParameters";
import useJStyles from "$hooks/useJStyles";
import { getDockyards } from "$services/Dockyard.service";
import useAuth from "$hooks/useAuth";
import { appState } from "$reducers";
import { getDockyardStatusOptions } from "$utils/typeConstants/Dockyard";
import DockyardStatusBadge from "./DockyardDetails/DockyardStatusBadge";
import { DockyardPopin } from "./DockyardPopin";
import { ImportButtonWithModal } from "$components/Forms/ImportButtonWithModal";
import { ImportType } from "$types/import/ImportType";
import { CacheInvalidationAction } from "$utils/cache";

const useMakeCell = (value, type, badge?) => {
  const navigate = useNavigate();
  const { id: companyId } = useParams() as any;
  const link = ({ _id }) =>
    companyId
      ? `/entreprise/${companyId}/chantiers/${_id}`
      : `/chantiers/${_id}`;

  return {
    type,
    link,
    value,
    badge,
    onClick: (row) => {
      navigate(link(row));
    },
  };
};

const Dockyards = () => {
  const [isActive, setisActive] = useState(true);
  const { jclasses } = useJStyles();
  const { Popin, PopinProps, togglePopin } = usePopin();
  const params = useQueryParameters();
  const { filter: companySidebarFilter } = useSelector(appState);
  const { isAdmin } = useAuth();

  const headerData = [
    {
      id: "companyName",
      filter: {
        type: EnumFilterTypes.INPUT,
        label: "Entreprise",
        placeholder: "Par nom",
        value: (companySidebarFilter?.name || params.get("companyName")) ?? "",
        disabled: !!companySidebarFilter?.name,
      },
      minWidth: 200,
      cell: {
        ...useMakeCell(({ company }) => company?.name, EnumDataRowType.Link),
        isDisabled: !isActive,
      },
    },
    {
      id: "nameCode",
      filter: {
        type: EnumFilterTypes.INPUT,
        label: "Chantier",
        placeholder: "Par nom ou code",
        value: params.get("nameCode"),
      },
      minWidth: 200,
      cell: {
        ...useMakeCell(({ code, name }) => {
          return { title: code, subtitle: name };
        }, EnumDataRowType.Link),
        isDisabled: !isActive,
      },
    },
    {
      id: "project",
      filter: {
        type: EnumFilterTypes.INPUT,
        label: "Projet",
        placeholder: "Par nom ou code",
        value: params.get("project"),
      },
      minWidth: 170,
      cell: {
        ...useMakeCell(({ project }) => {
          if (!project) {
            return { title: "Aucun projet associé", subtitle: null };
          }
          return {
            title: project?.code || "N/A",
            subtitle: project?.name || "N/A",
          };
        }, EnumDataRowType.EmptyLink),
        isDisabled: !isActive,
      },
    },
    {
      id: "status",
      filter: {
        type: EnumFilterTypes.SELECT,
        label: "Statut",
        value: params.get("status"),
        options: getDockyardStatusOptions(),
      },
      width: 80,
      cell: {
        ...useMakeCell(({ status }) => {
          return <DockyardStatusBadge disabled={!isActive} status={status} />;
        }, EnumDataRowType.Link),
        isDisabled: !isActive,
      },
    },
    {
      id: "approver",
      filter: {
        type: EnumFilterTypes.INPUT,
        label: "approbateur",
        placeholder: "Par nom",
        value: params.get("approver"),
      },
      minWidth: 120,
      cell: {
        ...useMakeCell(
          ({ approver }) =>
            approver
              ? `${approver.firstname || ""} ${
                  approver.lastname?.toUpperCase() || ""
                }`.trim() || "N/A"
              : "N/A",
          ""
        ),
        isDisabled: !isActive,
      },
    },
  ];

  const handleSwitch = () => {
    setisActive(!isActive);
  };

  const externalState = useMemo(() => {
    const returnState = {};

    for (const [key, value] of params.entries()) {
      returnState[key] = value;
    }
    const { page, rowsPerPage, dates, ...filters } = returnState as any;

    const parsedDates = dates ? JSON.parse(dates) : {};

    return {
      filters: {
        ...filters,
        isActive,
        ...(parsedDates.startDate && { startDate: parsedDates.startDate }),
        ...(parsedDates.endDate && { endDate: parsedDates.endDate }),
        ...(isAdmin() &&
          companySidebarFilter?._id && {
            company: companySidebarFilter?._id,
          }),
      },
      pagination: { page, rowsPerPage },
    };
  }, [isActive, isAdmin, companySidebarFilter?._id, params]);

  return (
    <Box className={jclasses.jWrapper}>
      <Layout
        title="Liste des chantiers"
        contentClassName={jclasses.jLayoutCustom}
        isHeaderRight
      >
        <Paper className={jclasses.jPaper}>
          <DataTableWithContext
            externalState={externalState}
            filterList={[
              <Switch
                key="active-dockyards"
                label="Chantiers actifs"
                onChange={handleSwitch}
                checked={isActive}
              />,
            ]}
            buttonList={[
              <Button
                key="add-dockyard"
                variant="outlined"
                onClick={togglePopin}
                startIcon={<AddIcon />}
                disabled={!isAdmin()}
              >
                Ajouter un chantier
              </Button>,
              <ImportButtonWithModal
                key="import-dockyards"
                title="Opérations en masse"
                dataName="chantiers"
                importType={ImportType.DOCKYARD_MULTIPLE}
                disabled={!isAdmin()}
                buttonTexts={{
                  importButtonText: "Importer des chantiers",
                  updateButtonText: "Mettre à jour des chantiers",
                }}
              />,
            ]}
            rowsPerPage={15}
            thead={headerData}
            queryFunction={getDockyards}
            queryFunctionName={CacheInvalidationAction.GET_DOCKYARDS}
            data-testid="dockyardsList"
          />
        </Paper>
        <Popin {...PopinProps}>
          <DockyardPopin togglePopin={togglePopin} />
        </Popin>
      </Layout>
    </Box>
  );
};

export default Dockyards;
